/**
 * portalRegistry — the SINGLE generated source for portal modules + navigation.
 *
 * Generated by codaic portal_generator (Portal v2, DEC-063). Do not hand-edit
 * structure by hand unless the portal is codegen-exempt; `codaic add service`
 * and `codaic init-wiki` re-emit / patch this file.
 *
 * Everything else derives from here at runtime: sidebar tree (Navigation),
 * horizontal SectionSubTabs, breadcrumb labels, module colors, search index.
 */

import React from 'react';
import { BookOpen, Box as BoxIcon, Calculator, FileText, PieChart, Settings, Shield, TrendingUp, Bot, BookMarked, ClipboardList, Gauge, Landmark, Scale, Workflow } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ModuleId = 'reference-data' | 'market-data' | 'instrument' | 'risk' | 'structured-products' | 'alm' | 'collateral' | 'operations' | 'limits-compliance' | 'accounting' | 'reporting' | 'order-management' | 'portfolio' | 'qfinpricer-chief' | 'wiki';

export interface EntityRef {
  name: string;
  label: string;
  endpoint: string;
  hasTimestamps: boolean;
}

export interface ModuleConfig {
  id: ModuleId;
  label: string;
  accent: string;
  icon: string;
  description: string;
  /** Entity metadata for overview widgets (P2): CRUD list endpoints per module. */
  entities: EntityRef[];
}

export interface NavigationItem {
  label: string;
  path: string;
  children?: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  label: string;
  accent: string;
  iconName: string;
  children: NavigationItem[];
}

// ── Modules ────────────────────────────────────────────────────────────────────

export const MODULE_CONFIGS: Record<ModuleId, ModuleConfig> = {
  'reference-data': {
    id: 'reference-data',
    label: 'Reference Data',
    accent: '#4fc3f7',
    icon: 'BookOpen',
    description: 'Reference Data entities',
    entities: [{"name": "Book", "endpoint": "books", "label": "Book", "hasTimestamps": true}, {"name": "Broker", "endpoint": "brokers", "label": "Broker", "hasTimestamps": true}, {"name": "BusinessDayConvention", "endpoint": "business-day-conventions", "label": "Business Day Convention", "hasTimestamps": true}, {"name": "Calendar", "endpoint": "calendars", "label": "Calendar", "hasTimestamps": true}, {"name": "Compounding", "endpoint": "compoundings", "label": "Compounding", "hasTimestamps": true}, {"name": "Counterparty", "endpoint": "counterparties", "label": "Counterparty", "hasTimestamps": true}, {"name": "Country", "endpoint": "countries", "label": "Country", "hasTimestamps": true}, {"name": "CpiInterpolationType", "endpoint": "cpi-interpolation-types", "label": "CPI Interpolation Type", "hasTimestamps": true}, {"name": "CreditReferenceEntity", "endpoint": "credit-reference-entities", "label": "Credit Reference Entity", "hasTimestamps": true}, {"name": "Currency", "endpoint": "currencies", "label": "Currency", "hasTimestamps": true}, {"name": "CurrencyPair", "endpoint": "currency-pairs", "label": "Currency Pair", "hasTimestamps": true}, {"name": "DateGenerationRule", "endpoint": "date-generation-rules", "label": "Date Generation Rule", "hasTimestamps": true}, {"name": "DayCountConvention", "endpoint": "day-count-conventions", "label": "Day Count Convention", "hasTimestamps": true}, {"name": "Desk", "endpoint": "desks", "label": "Desk", "hasTimestamps": true}, {"name": "DurationType", "endpoint": "duration-types", "label": "Duration Type", "hasTimestamps": true}, {"name": "Exchange", "endpoint": "exchanges", "label": "Exchange", "hasTimestamps": true}, {"name": "ExerciseType", "endpoint": "exercise-types", "label": "Exercise Type", "hasTimestamps": true}, {"name": "PaymentFrequency", "endpoint": "payment-frequencies", "label": "Payment Frequency", "hasTimestamps": true}, {"name": "Holiday", "endpoint": "holidays", "label": "Holiday", "hasTimestamps": true}, {"name": "InflationIndex", "endpoint": "inflation-indexes", "label": "Inflation Index", "hasTimestamps": true}, {"name": "InstrumentType", "endpoint": "instrument-types", "label": "Instrument Type", "hasTimestamps": true}, {"name": "InterestRateIndex", "endpoint": "interest-rate-indexes", "label": "Interest Rate Index", "hasTimestamps": true}, {"name": "LegalEntity", "endpoint": "legal-entities", "label": "Legal Entity", "hasTimestamps": true}, {"name": "MasterAgreement", "endpoint": "master-agreements", "label": "Master Agreement", "hasTimestamps": true}, {"name": "OptionType", "endpoint": "option-types", "label": "Option Type", "hasTimestamps": true}, {"name": "PartyRole", "endpoint": "party-roles", "label": "Party Role", "hasTimestamps": true}, {"name": "RateAveragingType", "endpoint": "rate-averaging-types", "label": "Rate Averaging Type", "hasTimestamps": true}, {"name": "RestructuringClause", "endpoint": "restructuring-clauses", "label": "Restructuring Clause", "hasTimestamps": true}, {"name": "VolatilityType", "endpoint": "volatility-types", "label": "Volatility Type", "hasTimestamps": true}],
  },
  'market-data': {
    id: 'market-data',
    label: 'Market Data',
    accent: '#66bb6a',
    icon: 'TrendingUp',
    description: 'Market Data entities',
    entities: [{"name": "CreditCurvePoint", "endpoint": "credit-curve-points", "label": "Credit Curve Point", "hasTimestamps": true}, {"name": "CreditSpreadCurve", "endpoint": "credit-spread-curves", "label": "Credit Spread Curve", "hasTimestamps": true}, {"name": "CreditSpreadPoint", "endpoint": "credit-spread-points", "label": "Credit Spread Point", "hasTimestamps": true}, {"name": "CreditSpreadSnapshot", "endpoint": "credit-spread-snapshots", "label": "Credit Spread Snapshot", "hasTimestamps": true}, {"name": "CurveDefinition", "endpoint": "curve-definitions", "label": "Curve Definition", "hasTimestamps": true}, {"name": "CurveInstrument", "endpoint": "curve-instruments", "label": "Curve Instrument", "hasTimestamps": true}, {"name": "CurvePoint", "endpoint": "curve-points", "label": "Curve Point", "hasTimestamps": true}, {"name": "CurveSnapshot", "endpoint": "curve-snapshots", "label": "Curve Snapshot", "hasTimestamps": true}, {"name": "Fixing", "endpoint": "fixings", "label": "Fixing", "hasTimestamps": true}, {"name": "FxSpot", "endpoint": "fx-spots", "label": "Fx Spot", "hasTimestamps": true}, {"name": "MarketDataSet", "endpoint": "market-data-sets", "label": "Market Data Set", "hasTimestamps": true}, {"name": "MarketQuote", "endpoint": "market-quotes", "label": "Market Quote", "hasTimestamps": true}, {"name": "VolSurface", "endpoint": "vol-surfaces", "label": "Vol Surface", "hasTimestamps": true}, {"name": "VolSurfacePoint", "endpoint": "vol-surface-points", "label": "Vol Surface Point", "hasTimestamps": true}, {"name": "VolSurfaceQuote", "endpoint": "vol-surface-quotes", "label": "Vol Surface Quote", "hasTimestamps": true}, {"name": "VolSurfaceSnapshot", "endpoint": "vol-surface-snapshots", "label": "Vol Surface Snapshot", "hasTimestamps": true}],
  },
  'instrument': {
    id: 'instrument',
    label: 'Instrument',
    accent: '#ff9800',
    icon: 'FileText',
    description: 'Instrument entities',
    entities: [{"name": "AmendAudit", "endpoint": "amend-audits", "label": "Amend Audit", "hasTimestamps": true}, {"name": "AmendRequest", "endpoint": "amend-requests", "label": "Amend Request", "hasTimestamps": true}, {"name": "Bond", "endpoint": "bonds", "label": "Bond", "hasTimestamps": true}, {"name": "BondTrade", "endpoint": "bond-trades", "label": "Bond Trade", "hasTimestamps": true}, {"name": "CapFloor", "endpoint": "cap-floors", "label": "Cap Floor", "hasTimestamps": true}, {"name": "CapFloorTrade", "endpoint": "cap-floor-trades", "label": "Cap Floor Trade", "hasTimestamps": true}, {"name": "CDS", "endpoint": "cdses", "label": "C D S", "hasTimestamps": true}, {"name": "CdsTrade", "endpoint": "cds-trades", "label": "Cds Trade", "hasTimestamps": true}, {"name": "CommodityTrade", "endpoint": "commodity-trades", "label": "Commodity Trade", "hasTimestamps": true}, {"name": "CorporateAction", "endpoint": "corporate-actions", "label": "Corporate Action", "hasTimestamps": true}, {"name": "CreditEvent", "endpoint": "credit-events", "label": "Credit Event", "hasTimestamps": true}, {"name": "EqOptionTrade", "endpoint": "eq-option-trades", "label": "Eq Option Trade", "hasTimestamps": true}, {"name": "EquityTrade", "endpoint": "equity-trades", "label": "Equity Trade", "hasTimestamps": true}, {"name": "ExerciseEvent", "endpoint": "exercise-events", "label": "Exercise Event", "hasTimestamps": true}, {"name": "ExoticOption", "endpoint": "exotic-options", "label": "Exotic Option", "hasTimestamps": true}, {"name": "ExoticOptionTrade", "endpoint": "exotic-option-trades", "label": "Exotic Option Trade", "hasTimestamps": true}, {"name": "FRA", "endpoint": "fras", "label": "F R A", "hasTimestamps": true}, {"name": "FuturesContract", "endpoint": "futures-contracts", "label": "Futures Contract", "hasTimestamps": true}, {"name": "FxInstrument", "endpoint": "fx-instruments", "label": "Fx Instrument", "hasTimestamps": true}, {"name": "FxOptionTrade", "endpoint": "fx-option-trades", "label": "Fx Option Trade", "hasTimestamps": true}, {"name": "FxTrade", "endpoint": "fx-trades", "label": "Fx Trade", "hasTimestamps": true}, {"name": "Instrument", "endpoint": "instruments", "label": "Instrument", "hasTimestamps": true}, {"name": "InstrumentCashflow", "endpoint": "instrument-cashflows", "label": "Instrument Cashflow", "hasTimestamps": true}, {"name": "IrSwapTrade", "endpoint": "ir-swap-trades", "label": "Ir Swap Trade", "hasTimestamps": true}, {"name": "Option", "endpoint": "options", "label": "Option", "hasTimestamps": true}, {"name": "PartyTradeRole", "endpoint": "party-trade-roles", "label": "Party Trade Role", "hasTimestamps": true}, {"name": "Portfolio", "endpoint": "portfolios", "label": "Portfolio", "hasTimestamps": true}, {"name": "PortfolioPosition", "endpoint": "portfolio-positions", "label": "Portfolio Position", "hasTimestamps": true}, {"name": "RepoTrade", "endpoint": "repo-trades", "label": "Repo Trade", "hasTimestamps": true}, {"name": "Schedule", "endpoint": "schedules", "label": "Schedule", "hasTimestamps": true}, {"name": "Swap", "endpoint": "swaps", "label": "Swap", "hasTimestamps": true}, {"name": "SwapLeg", "endpoint": "swap-legs", "label": "Swap Leg", "hasTimestamps": true}, {"name": "Swaption", "endpoint": "swaptions", "label": "Swaption", "hasTimestamps": true}, {"name": "SwaptionTrade", "endpoint": "swaption-trades", "label": "Swaption Trade", "hasTimestamps": true}, {"name": "Trade", "endpoint": "trades", "label": "Trade", "hasTimestamps": true}, {"name": "TradeAmendment", "endpoint": "trade-amendments", "label": "Trade Amendment", "hasTimestamps": true}, {"name": "TradeComment", "endpoint": "trade-comments", "label": "Trade Comment", "hasTimestamps": true}, {"name": "TradeConfirmation", "endpoint": "trade-confirmations", "label": "Trade Confirmation", "hasTimestamps": true}, {"name": "TradeEvent", "endpoint": "trade-events", "label": "Trade Event", "hasTimestamps": true}, {"name": "TradeFee", "endpoint": "trade-fees", "label": "Trade Fee", "hasTimestamps": true}, {"name": "TradeNettingMembership", "endpoint": "trade-netting-memberships", "label": "Trade Netting Membership", "hasTimestamps": true}, {"name": "TradeReset", "endpoint": "trade-resets", "label": "Trade Reset", "hasTimestamps": true}, {"name": "TradeSource", "endpoint": "trade-sources", "label": "Trade Source", "hasTimestamps": true}, {"name": "TradeVersion", "endpoint": "trade-versions", "label": "Trade Version", "hasTimestamps": true}, {"name": "TradeWorkflow", "endpoint": "trade-workflows", "label": "Trade Workflow", "hasTimestamps": true}, {"name": "TrsTrade", "endpoint": "trs-trades", "label": "Trs Trade", "hasTimestamps": true}],
  },
  'risk': {
    id: 'risk',
    label: 'Risk',
    accent: '#f44336',
    icon: 'Shield',
    description: 'Risk entities',
    entities: [{"name": "CalibrationResult", "endpoint": "calibration-results", "label": "Calibration Result", "hasTimestamps": true}, {"name": "GreeksSnapshot", "endpoint": "greeks-snapshots", "label": "Greeks Snapshot", "hasTimestamps": true}, {"name": "ModelDefinition", "endpoint": "model-definitions", "label": "Model Definition", "hasTimestamps": true}, {"name": "PricingConfiguration", "endpoint": "pricing-configurations", "label": "Pricing Configuration", "hasTimestamps": true}, {"name": "PricingRequest", "endpoint": "pricing-requests", "label": "Pricing Request", "hasTimestamps": true}, {"name": "PricingResult", "endpoint": "pricing-results", "label": "Pricing Result", "hasTimestamps": true}, {"name": "RiskMetric", "endpoint": "risk-metrics", "label": "Risk Metric", "hasTimestamps": true}, {"name": "Scenario", "endpoint": "scenarios", "label": "Scenario", "hasTimestamps": true}, {"name": "ScenarioResult", "endpoint": "scenario-results", "label": "Scenario Result", "hasTimestamps": true}, {"name": "SensitivityGrid", "endpoint": "sensitivity-grids", "label": "Sensitivity Grid", "hasTimestamps": true}, {"name": "StressTest", "endpoint": "stress-tests", "label": "Stress Test", "hasTimestamps": true}, {"name": "StressTestResult", "endpoint": "stress-test-results", "label": "Stress Test Result", "hasTimestamps": true}],
  },
  'structured-products': {
    id: 'structured-products',
    label: 'Structured Products',
    accent: '#ab47bc',
    icon: 'Box',
    description: 'Structured Products entities',
    entities: [{"name": "AccumulatorConfig", "endpoint": "accumulator-configs", "label": "Accumulator Config", "hasTimestamps": true}, {"name": "Approval", "endpoint": "approvals", "label": "Approval", "hasTimestamps": true}, {"name": "AuditTrail", "endpoint": "audit-trails", "label": "Audit Trail", "hasTimestamps": true}, {"name": "Barrier", "endpoint": "barriers", "label": "Barrier", "hasTimestamps": true}, {"name": "Cashflow", "endpoint": "cashflows", "label": "Cashflow", "hasTimestamps": true}, {"name": "CommodityLinked", "endpoint": "commodity-linkeds", "label": "Commodity Linked", "hasTimestamps": true}, {"name": "CouponSchedule", "endpoint": "coupon-schedules", "label": "Coupon Schedule", "hasTimestamps": true}, {"name": "CreditLinked", "endpoint": "credit-linkeds", "label": "Credit Linked", "hasTimestamps": true}, {"name": "EquityStructured", "endpoint": "equity-structureds", "label": "Equity Structured", "hasTimestamps": true}, {"name": "Fixing", "endpoint": "fixings", "label": "Fixing", "hasTimestamps": true}, {"name": "FxStructured", "endpoint": "fx-structureds", "label": "Fx Structured", "hasTimestamps": true}, {"name": "Greeks", "endpoint": "greekses", "label": "Greeks", "hasTimestamps": true}, {"name": "IrdStructured", "endpoint": "ird-structureds", "label": "Ird Structured", "hasTimestamps": true}, {"name": "LifecycleEvent", "endpoint": "lifecycle-events", "label": "Lifecycle Event", "hasTimestamps": true}, {"name": "MandatoryConvertibleConfig", "endpoint": "mandatory-convertible-configs", "label": "Mandatory Convertible Config", "hasTimestamps": true}, {"name": "MarketDataSnapshot", "endpoint": "market-data-snapshots", "label": "Market Data Snapshot", "hasTimestamps": true}, {"name": "ObservationSchedule", "endpoint": "observation-schedules", "label": "Observation Schedule", "hasTimestamps": true}, {"name": "PricingRequestLog", "endpoint": "pricing-request-logs", "label": "Pricing Request Log", "hasTimestamps": true}, {"name": "PricingResult", "endpoint": "pricing-results", "label": "Pricing Result", "hasTimestamps": true}, {"name": "ProductLink", "endpoint": "product-links", "label": "Product Link", "hasTimestamps": true}, {"name": "ProductTemplate", "endpoint": "product-templates", "label": "Product Template", "hasTimestamps": true}, {"name": "RangeAccrualConfig", "endpoint": "range-accrual-configs", "label": "Range Accrual Config", "hasTimestamps": true}, {"name": "Scenario", "endpoint": "scenarios", "label": "Scenario", "hasTimestamps": true}, {"name": "ScenarioResult", "endpoint": "scenario-results", "label": "Scenario Result", "hasTimestamps": true}, {"name": "Settlement", "endpoint": "settlements", "label": "Settlement", "hasTimestamps": true}, {"name": "StructuredProduct", "endpoint": "structured-products", "label": "Structured Product", "hasTimestamps": true}, {"name": "TarfConfig", "endpoint": "tarf-configs", "label": "Tarf Config", "hasTimestamps": true}, {"name": "Termsheet", "endpoint": "termsheets", "label": "Termsheet", "hasTimestamps": true}, {"name": "Underlying", "endpoint": "underlyings", "label": "Underlying", "hasTimestamps": true}],
  },
  'alm': {
    id: 'alm',
    label: 'ALM',
    accent: '#3f51b5',
    icon: 'Scale',
    description: 'Alm entities',
    entities: [{"name": "ALMPosition", "endpoint": "alm-positions", "label": "A L M Position", "hasTimestamps": true}, {"name": "AuditTrail", "endpoint": "audit-trails", "label": "Audit Trail", "hasTimestamps": true}, {"name": "BaselCapitalRequirement", "endpoint": "basel-capital-requirements", "label": "Basel Capital Requirement", "hasTimestamps": true}, {"name": "BehavioralModel", "endpoint": "behavioral-models", "label": "Behavioral Model", "hasTimestamps": true}, {"name": "EVESimulation", "endpoint": "eve-simulations", "label": "E V E Simulation", "hasTimestamps": true}, {"name": "ExpectedPayment", "endpoint": "expected-payments", "label": "Expected Payment", "hasTimestamps": true}, {"name": "FTPAllocation", "endpoint": "ftp-allocations", "label": "F T P Allocation", "hasTimestamps": true}, {"name": "FTPRate", "endpoint": "ftp-rates", "label": "F T P Rate", "hasTimestamps": true}, {"name": "GapAnalysis", "endpoint": "gap-analysises", "label": "Gap Analysis", "hasTimestamps": true}, {"name": "LcrRunoffRate", "endpoint": "lcr-runoff-rates", "label": "Lcr Runoff Rate", "hasTimestamps": true}, {"name": "LiquidityCashflow", "endpoint": "liquidity-cashflows", "label": "Liquidity Cashflow", "hasTimestamps": true}, {"name": "LiquidityRatio", "endpoint": "liquidity-ratios", "label": "Liquidity Ratio", "hasTimestamps": true}, {"name": "MonteCarloSimulation", "endpoint": "monte-carlo-simulations", "label": "Monte Carlo Simulation", "hasTimestamps": true}, {"name": "NIISimulation", "endpoint": "nii-simulations", "label": "N I I Simulation", "hasTimestamps": true}, {"name": "ReconciliationResult", "endpoint": "reconciliation-results", "label": "Reconciliation Result", "hasTimestamps": true}, {"name": "ReconciliationRule", "endpoint": "reconciliation-rules", "label": "Reconciliation Rule", "hasTimestamps": true}, {"name": "RWACalculation", "endpoint": "rwa-calculations", "label": "R W A Calculation", "hasTimestamps": true}, {"name": "StressScenarioALM", "endpoint": "stress-scenario-alms", "label": "Stress Scenario A L M", "hasTimestamps": true}, {"name": "ToleranceRule", "endpoint": "tolerance-rules", "label": "Tolerance Rule", "hasTimestamps": true}, {"name": "Transaction", "endpoint": "transactions", "label": "Transaction", "hasTimestamps": true}, {"name": "TransactionLineItem", "endpoint": "transaction-line-items", "label": "Transaction Line Item", "hasTimestamps": true}],
  },
  'collateral': {
    id: 'collateral',
    label: 'Collateral',
    accent: '#00897b',
    icon: 'Landmark',
    description: 'Collateral entities',
    entities: [{"name": "CollateralBalance", "endpoint": "collateral-balances", "label": "Collateral Balance", "hasTimestamps": true}, {"name": "CollateralInventory", "endpoint": "collateral-inventories", "label": "Collateral Inventory", "hasTimestamps": true}, {"name": "CollateralMovement", "endpoint": "collateral-movements", "label": "Collateral Movement", "hasTimestamps": true}, {"name": "CsaAgreement", "endpoint": "csa-agreements", "label": "Csa Agreement", "hasTimestamps": true}, {"name": "CsaAmendmentHistory", "endpoint": "csa-amendment-histories", "label": "Csa Amendment History", "hasTimestamps": true}, {"name": "EligibleCollateral", "endpoint": "eligible-collaterals", "label": "Eligible Collateral", "hasTimestamps": true}, {"name": "MarginCall", "endpoint": "margin-calls", "label": "Margin Call", "hasTimestamps": true}, {"name": "MarginCallDispute", "endpoint": "margin-call-disputes", "label": "Margin Call Dispute", "hasTimestamps": true}, {"name": "NettingSet", "endpoint": "netting-sets", "label": "Netting Set", "hasTimestamps": true}, {"name": "NettingSetMember", "endpoint": "netting-set-members", "label": "Netting Set Member", "hasTimestamps": true}, {"name": "SimmCalculation", "endpoint": "simm-calculations", "label": "Simm Calculation", "hasTimestamps": true}, {"name": "SimmParameter", "endpoint": "simm-parameters", "label": "Simm Parameter", "hasTimestamps": true}, {"name": "SimmSensitivity", "endpoint": "simm-sensitivities", "label": "Simm Sensitivity", "hasTimestamps": true}, {"name": "ValuationAdjustment", "endpoint": "valuation-adjustments", "label": "Valuation Adjustment", "hasTimestamps": true}, {"name": "XvaResult", "endpoint": "xva-results", "label": "Xva Result", "hasTimestamps": true}],
  },
  'operations': {
    id: 'operations',
    label: 'Operations',
    accent: '#e65100',
    icon: 'Workflow',
    description: 'Operations entities',
    entities: [{"name": "BankStatement", "endpoint": "bank-statements", "label": "Bank Statement", "hasTimestamps": true}, {"name": "BankStatementLine", "endpoint": "bank-statement-lines", "label": "Bank Statement Line", "hasTimestamps": true}, {"name": "ConfirmationMatching", "endpoint": "confirmation-matchings", "label": "Confirmation Matching", "hasTimestamps": true}, {"name": "ConfirmationTemplate", "endpoint": "confirmation-templates", "label": "Confirmation Template", "hasTimestamps": true}, {"name": "CutoffTime", "endpoint": "cutoff-times", "label": "Cutoff Time", "hasTimestamps": true}, {"name": "EODRun", "endpoint": "eod-runs", "label": "E O D Run", "hasTimestamps": true}, {"name": "FixingManagement", "endpoint": "fixing-managements", "label": "Fixing Management", "hasTimestamps": true}, {"name": "NostroReconciliation", "endpoint": "nostro-reconciliations", "label": "Nostro Reconciliation", "hasTimestamps": true}, {"name": "OperationalAlert", "endpoint": "operational-alerts", "label": "Operational Alert", "hasTimestamps": true}, {"name": "OperationalCalendar", "endpoint": "operational-calendars", "label": "Operational Calendar", "hasTimestamps": true}, {"name": "Reconciliation", "endpoint": "reconciliations", "label": "Reconciliation", "hasTimestamps": true}, {"name": "ReconciliationBreak", "endpoint": "reconciliation-breaks", "label": "Reconciliation Break", "hasTimestamps": true}, {"name": "Reversal", "endpoint": "reversals", "label": "Reversal", "hasTimestamps": true}, {"name": "Settlement", "endpoint": "settlements", "label": "Settlement", "hasTimestamps": true}, {"name": "SettlementFail", "endpoint": "settlement-fails", "label": "Settlement Fail", "hasTimestamps": true}, {"name": "SettlementHold", "endpoint": "settlement-holds", "label": "Settlement Hold", "hasTimestamps": true}, {"name": "SettlementInstruction", "endpoint": "settlement-instructions", "label": "Settlement Instruction", "hasTimestamps": true}, {"name": "SettlementReferenceNumber", "endpoint": "settlement-reference-numbers", "label": "Settlement Reference Number", "hasTimestamps": true}, {"name": "StpExecution", "endpoint": "stp-executions", "label": "Stp Execution", "hasTimestamps": true}, {"name": "StpRule", "endpoint": "stp-rules", "label": "Stp Rule", "hasTimestamps": true}, {"name": "SwiftInterface", "endpoint": "swift-interfaces", "label": "Swift Interface", "hasTimestamps": true}, {"name": "SwiftMessageLog", "endpoint": "swift-message-logs", "label": "Swift Message Log", "hasTimestamps": true}],
  },
  'limits-compliance': {
    id: 'limits-compliance',
    label: 'Limits Compliance',
    accent: '#1565c0',
    icon: 'Gauge',
    description: 'Limits Compliance entities',
    entities: [{"name": "ComplianceCheck", "endpoint": "compliance-checks", "label": "Compliance Check", "hasTimestamps": true}, {"name": "ComplianceRule", "endpoint": "compliance-rules", "label": "Compliance Rule", "hasTimestamps": true}, {"name": "CounterpartyExposure", "endpoint": "counterparty-exposures", "label": "Counterparty Exposure", "hasTimestamps": true}, {"name": "ExceptionRequest", "endpoint": "exception-requests", "label": "Exception Request", "hasTimestamps": true}, {"name": "LimitApproval", "endpoint": "limit-approvals", "label": "Limit Approval", "hasTimestamps": true}, {"name": "LimitBreach", "endpoint": "limit-breaches", "label": "Limit Breach", "hasTimestamps": true}, {"name": "LimitDefinition", "endpoint": "limit-definitions", "label": "Limit Definition", "hasTimestamps": true}, {"name": "LimitException", "endpoint": "limit-exceptions", "label": "Limit Exception", "hasTimestamps": true}, {"name": "LimitHierarchy", "endpoint": "limit-hierarchies", "label": "Limit Hierarchy", "hasTimestamps": true}, {"name": "LimitUtilization", "endpoint": "limit-utilizations", "label": "Limit Utilization", "hasTimestamps": true}, {"name": "PortfolioRuleAssignment", "endpoint": "portfolio-rule-assignments", "label": "Portfolio Rule Assignment", "hasTimestamps": true}, {"name": "TradeSurveillanceAlert", "endpoint": "trade-surveillance-alerts", "label": "Trade Surveillance Alert", "hasTimestamps": true}],
  },
  'accounting': {
    id: 'accounting',
    label: 'Accounting',
    accent: '#c62828',
    icon: 'Calculator',
    description: 'Accounting entities',
    entities: [{"name": "Account", "endpoint": "accounts", "label": "Account", "hasTimestamps": true}, {"name": "AccountingPeriod", "endpoint": "accounting-periods", "label": "Accounting Period", "hasTimestamps": true}, {"name": "AccountingPosition", "endpoint": "accounting-positions", "label": "Accounting Position", "hasTimestamps": true}, {"name": "AccrualSchedule", "endpoint": "accrual-schedules", "label": "Accrual Schedule", "hasTimestamps": true}, {"name": "HedgeDesignation", "endpoint": "hedge-designations", "label": "Hedge Designation", "hasTimestamps": true}, {"name": "HedgeEffectivenessTest", "endpoint": "hedge-effectiveness-tests", "label": "Hedge Effectiveness Test", "hasTimestamps": true}, {"name": "JournalEntry", "endpoint": "journal-entries", "label": "Journal Entry", "hasTimestamps": true}, {"name": "JournalEntryLine", "endpoint": "journal-entry-lines", "label": "Journal Entry Line", "hasTimestamps": true}, {"name": "LedgerBalance", "endpoint": "ledger-balances", "label": "Ledger Balance", "hasTimestamps": true}, {"name": "PeriodCloseTask", "endpoint": "period-close-tasks", "label": "Period Close Task", "hasTimestamps": true}, {"name": "PostingRule", "endpoint": "posting-rules", "label": "Posting Rule", "hasTimestamps": true}, {"name": "RevaluationEntry", "endpoint": "revaluation-entries", "label": "Revaluation Entry", "hasTimestamps": true}],
  },
  'reporting': {
    id: 'reporting',
    label: 'Reporting',
    accent: '#00695c',
    icon: 'FileBarChart',
    description: 'Reports, dashboards, and regulatory returns',
    entities: [{"name": "Dashboard", "endpoint": "dashboards", "label": "Dashboard", "hasTimestamps": true}, {"name": "DashboardTile", "endpoint": "dashboard-tiles", "label": "Dashboard Tile", "hasTimestamps": true}, {"name": "RegulatoryReturn", "endpoint": "regulatory-returns", "label": "Regulatory Return", "hasTimestamps": true}, {"name": "RegulatoryReturnFiling", "endpoint": "regulatory-return-filings", "label": "Regulatory Return Filing", "hasTimestamps": true}, {"name": "ReportDataSource", "endpoint": "report-data-sources", "label": "Report Data Source", "hasTimestamps": true}, {"name": "ReportDefinition", "endpoint": "report-definitions", "label": "Report Definition", "hasTimestamps": true}, {"name": "ReportDistribution", "endpoint": "report-distributions", "label": "Report Distribution", "hasTimestamps": true}, {"name": "ReportOutput", "endpoint": "report-outputs", "label": "Report Output", "hasTimestamps": true}, {"name": "ReportParameter", "endpoint": "report-parameters", "label": "Report Parameter", "hasTimestamps": true}, {"name": "ReportRun", "endpoint": "report-runs", "label": "Report Run", "hasTimestamps": true}, {"name": "ReportSchedule", "endpoint": "report-schedules", "label": "Report Schedule", "hasTimestamps": true}],
  },
  'order-management': {
    id: 'order-management',
    label: 'Order Management',
    accent: '#6a1b9a',
    icon: 'ClipboardList',
    description: 'Order Management entities',
    entities: [{"name": "AllocationInstruction", "endpoint": "allocation-instructions", "label": "Allocation Instruction", "hasTimestamps": true}, {"name": "AllocationLine", "endpoint": "allocation-lines", "label": "Allocation Line", "hasTimestamps": true}, {"name": "AllocationTemplate", "endpoint": "allocation-templates", "label": "Allocation Template", "hasTimestamps": true}, {"name": "Execution", "endpoint": "executions", "label": "Execution", "hasTimestamps": true}, {"name": "Fill", "endpoint": "fills", "label": "Fill", "hasTimestamps": true}, {"name": "Order", "endpoint": "orders", "label": "Order", "hasTimestamps": true}, {"name": "OrderAuditEvent", "endpoint": "order-audit-events", "label": "Order Audit Event", "hasTimestamps": true}, {"name": "OrderComplianceCheck", "endpoint": "order-compliance-checks", "label": "Order Compliance Check", "hasTimestamps": true}, {"name": "OrderVersion", "endpoint": "order-versions", "label": "Order Version", "hasTimestamps": true}, {"name": "RoutingRule", "endpoint": "routing-rules", "label": "Routing Rule", "hasTimestamps": true}],
  },
  'portfolio': {
    id: 'portfolio',
    label: 'Portfolio',
    accent: '#2e7d32',
    icon: 'PieChart',
    description: 'Portfolio entities',
    entities: [{"name": "AggregationRule", "endpoint": "aggregation-rules", "label": "Aggregation Rule", "hasTimestamps": true}, {"name": "CalcInputSnapshot", "endpoint": "calc-input-snapshots", "label": "Calc Input Snapshot", "hasTimestamps": true}, {"name": "CorporateActionElection", "endpoint": "corporate-action-elections", "label": "Corporate Action Election", "hasTimestamps": true}, {"name": "CorporateActionEntitlement", "endpoint": "corporate-action-entitlements", "label": "Corporate Action Entitlement", "hasTimestamps": true}, {"name": "FuturesPosition", "endpoint": "futures-positions", "label": "Futures Position", "hasTimestamps": true}, {"name": "Holding", "endpoint": "holdings", "label": "Holding", "hasTimestamps": true}, {"name": "Methodology", "endpoint": "methodologies", "label": "Methodology", "hasTimestamps": true}, {"name": "MultiBook", "endpoint": "multi-books", "label": "Multi Book", "hasTimestamps": true}, {"name": "PerformanceClose", "endpoint": "performance-closes", "label": "Performance Close", "hasTimestamps": true}, {"name": "PerformanceRun", "endpoint": "performance-runs", "label": "Performance Run", "hasTimestamps": true}, {"name": "PnlWaterfall", "endpoint": "pnl-waterfalls", "label": "Pnl Waterfall", "hasTimestamps": true}, {"name": "PortfolioBookMap", "endpoint": "portfolio-book-maps", "label": "Portfolio Book Map", "hasTimestamps": true}, {"name": "PortfolioValuation", "endpoint": "portfolio-valuations", "label": "Portfolio Valuation", "hasTimestamps": true}, {"name": "Position", "endpoint": "positions", "label": "Position", "hasTimestamps": true}, {"name": "PositionFX", "endpoint": "position-fxes", "label": "Position F X", "hasTimestamps": true}, {"name": "PositionIR", "endpoint": "position-irs", "label": "Position I R", "hasTimestamps": true}, {"name": "PositionSnapshot", "endpoint": "position-snapshots", "label": "Position Snapshot", "hasTimestamps": true}, {"name": "PositionTransaction", "endpoint": "position-transactions", "label": "Position Transaction", "hasTimestamps": true}, {"name": "Rbor", "endpoint": "rbors", "label": "Rbor", "hasTimestamps": true}, {"name": "RiskAdjustedMetric", "endpoint": "risk-adjusted-metrics", "label": "Risk Adjusted Metric", "hasTimestamps": true}, {"name": "ValuationResult", "endpoint": "valuation-results", "label": "Valuation Result", "hasTimestamps": true}],
  },
  'qfinpricer-chief': {
    id: 'qfinpricer-chief',
    label: 'Chief Agent',
    accent: '#607d8b',
    icon: 'Bot',
    description: 'Qfinpricer Chief entities',
    entities: [{"name": "Agent", "endpoint": "agents", "label": "Agent", "hasTimestamps": true}, {"name": "AgentInvocation", "endpoint": "agent-invocations", "label": "Agent Invocation", "hasTimestamps": true}, {"name": "ExecutionStep", "endpoint": "execution-steps", "label": "Execution Step", "hasTimestamps": true}, {"name": "OrchestratorChat", "endpoint": "orchestrator-chats", "label": "Orchestrator Chat", "hasTimestamps": true}],
  },
  'wiki': {
    id: 'wiki',
    label: 'Knowledge Wiki',
    accent: '#8b5cf6',
    icon: 'BookMarked',
    description: 'Wiki entities',
    entities: [{"name": "Link", "endpoint": "links", "label": "Link", "hasTimestamps": true}, {"name": "Log", "endpoint": "logs", "label": "Log", "hasTimestamps": true}, {"name": "Page", "endpoint": "pages", "label": "Page", "hasTimestamps": true}, {"name": "Source", "endpoint": "sources", "label": "Source", "hasTimestamps": true}],
  },
};

export const MODULE_LIST: ModuleConfig[] = Object.values(MODULE_CONFIGS);

// ── Navigation tree ────────────────────────────────────────────────────────────

export const navigationConfig: NavigationSection[] = [
  {
    "id": "reference-data",
    "label": "Reference Data",
    "accent": "#4fc3f7",
    "iconName": "BookOpen",
    "children": [
      {
        "label": "Overview",
        "path": "/reference-data"
      },
      {
        "label": "Dashboard",
        "path": "/reference-data/dashboard"
      },
      {
        "label": "Entities",
        "path": "/reference-data/admin",
        "children": [
          {
            "label": "Book",
            "path": "/reference-data/admin/books"
          },
          {
            "label": "Broker",
            "path": "/reference-data/admin/brokers"
          },
          {
            "label": "Business Day Convention",
            "path": "/reference-data/admin/business-day-conventions"
          },
          {
            "label": "Calendar",
            "path": "/reference-data/admin/calendars"
          },
          {
            "label": "Compounding",
            "path": "/reference-data/admin/compoundings"
          },
          {
            "label": "Counterparty",
            "path": "/reference-data/admin/counterparties"
          },
          {
            "label": "Country",
            "path": "/reference-data/admin/countries"
          },
          {
            "label": "C P I Interpolation Type",
            "path": "/reference-data/admin/cpi-interpolation-types"
          },
          {
            "label": "Credit Reference Entity",
            "path": "/reference-data/admin/credit-reference-entities"
          },
          {
            "label": "Currency",
            "path": "/reference-data/admin/currencies"
          },
          {
            "label": "Currency Pair",
            "path": "/reference-data/admin/currency-pairs"
          },
          {
            "label": "Date Generation Rule",
            "path": "/reference-data/admin/date-generation-rules"
          },
          {
            "label": "Day Count Convention",
            "path": "/reference-data/admin/day-count-conventions"
          },
          {
            "label": "Desk",
            "path": "/reference-data/admin/desks"
          },
          {
            "label": "Duration Type",
            "path": "/reference-data/admin/duration-types"
          },
          {
            "label": "Exchange",
            "path": "/reference-data/admin/exchanges"
          },
          {
            "label": "Exercise Type",
            "path": "/reference-data/admin/exercise-types"
          },
          {
            "label": "Payment Frequency",
            "path": "/reference-data/admin/payment-frequencies"
          },
          {
            "label": "Holiday",
            "path": "/reference-data/admin/holidays"
          },
          {
            "label": "Inflation Index",
            "path": "/reference-data/admin/inflation-indexes"
          },
          {
            "label": "Instrument Type",
            "path": "/reference-data/admin/instrument-types"
          },
          {
            "label": "Interest Rate Index",
            "path": "/reference-data/admin/interest-rate-indexes"
          },
          {
            "label": "Legal Entity",
            "path": "/reference-data/admin/legal-entities"
          },
          {
            "label": "Master Agreement",
            "path": "/reference-data/admin/master-agreements"
          },
          {
            "label": "Option Type",
            "path": "/reference-data/admin/option-types"
          },
          {
            "label": "Party Role",
            "path": "/reference-data/admin/party-roles"
          },
          {
            "label": "Rate Averaging Type",
            "path": "/reference-data/admin/rate-averaging-types"
          },
          {
            "label": "Restructuring Clause",
            "path": "/reference-data/admin/restructuring-clauses"
          },
          {
            "label": "Volatility Type",
            "path": "/reference-data/admin/volatility-types"
          }
        ]
      }
    ]
  },
  {
    "id": "market-data",
    "label": "Market Data",
    "accent": "#66bb6a",
    "iconName": "TrendingUp",
    "children": [
      {
        "label": "Overview",
        "path": "/market-data"
      },
      {
        "label": "Dashboard",
        "path": "/market-data/dashboard"
      },
      {
        "label": "Curves",
        "path": "/market-data/curves"
      },
      {
        "label": "Curve Builder Legacy",
        "path": "/market-data/curve-builder-legacy"
      },
      {
        "label": "Credit Curve Builder Legacy",
        "path": "/market-data/credit-curve-builder-legacy"
      },
      {
        "label": "Vol Surface Viewer Legacy",
        "path": "/market-data/vol-surface-viewer-legacy"
      },
      {
        "label": "Curve Points",
        "path": "/market-data/admin/curve-points"
      },
      {
        "label": "Credit Curve Points",
        "path": "/market-data/admin/credit-curve-points"
      },
      {
        "label": "Entities",
        "path": "/market-data/admin",
        "children": [
          {
            "label": "Credit Curve Point",
            "path": "/market-data/admin/credit-curve-points"
          },
          {
            "label": "Credit Spread Curve",
            "path": "/market-data/admin/credit-spread-curves"
          },
          {
            "label": "Credit Spread Point",
            "path": "/market-data/admin/credit-spread-points"
          },
          {
            "label": "Credit Spread Snapshot",
            "path": "/market-data/admin/credit-spread-snapshots"
          },
          {
            "label": "Curve Definition",
            "path": "/market-data/admin/curve-definitions"
          },
          {
            "label": "Curve Instrument",
            "path": "/market-data/admin/curve-instruments"
          },
          {
            "label": "Curve Point",
            "path": "/market-data/admin/curve-points"
          },
          {
            "label": "Curve Snapshot",
            "path": "/market-data/admin/curve-snapshots"
          },
          {
            "label": "Fixing",
            "path": "/market-data/admin/fixings"
          },
          {
            "label": "Fx Spot",
            "path": "/market-data/admin/fx-spots"
          },
          {
            "label": "Market Data Set",
            "path": "/market-data/admin/market-data-sets"
          },
          {
            "label": "Market Quote",
            "path": "/market-data/admin/market-quotes"
          },
          {
            "label": "Vol Surface",
            "path": "/market-data/admin/vol-surfaces"
          },
          {
            "label": "Vol Surface Point",
            "path": "/market-data/admin/vol-surface-points"
          },
          {
            "label": "Vol Surface Quote",
            "path": "/market-data/admin/vol-surface-quotes"
          },
          {
            "label": "Vol Surface Snapshot",
            "path": "/market-data/admin/vol-surface-snapshots"
          }
        ]
      }
    ]
  },
  {
    "id": "instrument",
    "label": "Instrument",
    "accent": "#ff9800",
    "iconName": "FileText",
    "children": [
      {
        "label": "Overview",
        "path": "/instrument"
      },
      {
        "label": "Dashboard",
        "path": "/instrument/dashboard"
      },
      {
        "label": "Entities",
        "path": "/instrument/admin",
        "children": [
          {
            "label": "Amend Audit",
            "path": "/instrument/admin/amend-audits"
          },
          {
            "label": "Amend Request",
            "path": "/instrument/admin/amend-requests"
          },
          {
            "label": "Bond",
            "path": "/instrument/admin/bonds"
          },
          {
            "label": "Bond Trade",
            "path": "/instrument/admin/bond-trades"
          },
          {
            "label": "Cap Floor",
            "path": "/instrument/admin/cap-floors"
          },
          {
            "label": "Cap Floor Trade",
            "path": "/instrument/admin/cap-floor-trades"
          },
          {
            "label": "C D S",
            "path": "/instrument/admin/cdses"
          },
          {
            "label": "Cds Trade",
            "path": "/instrument/admin/cds-trades"
          },
          {
            "label": "Commodity Trade",
            "path": "/instrument/admin/commodity-trades"
          },
          {
            "label": "Corporate Action",
            "path": "/instrument/admin/corporate-actions"
          },
          {
            "label": "Credit Event",
            "path": "/instrument/admin/credit-events"
          },
          {
            "label": "Eq Option Trade",
            "path": "/instrument/admin/eq-option-trades"
          },
          {
            "label": "Equity Trade",
            "path": "/instrument/admin/equity-trades"
          },
          {
            "label": "Exercise Event",
            "path": "/instrument/admin/exercise-events"
          },
          {
            "label": "Exotic Option",
            "path": "/instrument/admin/exotic-options"
          },
          {
            "label": "Exotic Option Trade",
            "path": "/instrument/admin/exotic-option-trades"
          },
          {
            "label": "F R A",
            "path": "/instrument/admin/fras"
          },
          {
            "label": "Futures Contract",
            "path": "/instrument/admin/futures-contracts"
          },
          {
            "label": "Fx Instrument",
            "path": "/instrument/admin/fx-instruments"
          },
          {
            "label": "Fx Option Trade",
            "path": "/instrument/admin/fx-option-trades"
          },
          {
            "label": "Fx Trade",
            "path": "/instrument/admin/fx-trades"
          },
          {
            "label": "Instrument",
            "path": "/instrument/admin/instruments"
          },
          {
            "label": "Instrument Cashflow",
            "path": "/instrument/admin/instrument-cashflows"
          },
          {
            "label": "Ir Swap Trade",
            "path": "/instrument/admin/ir-swap-trades"
          },
          {
            "label": "Option",
            "path": "/instrument/admin/options"
          },
          {
            "label": "Party Trade Role",
            "path": "/instrument/admin/party-trade-roles"
          },
          {
            "label": "Portfolio",
            "path": "/instrument/admin/portfolios"
          },
          {
            "label": "Portfolio Position",
            "path": "/instrument/admin/portfolio-positions"
          },
          {
            "label": "Repo Trade",
            "path": "/instrument/admin/repo-trades"
          },
          {
            "label": "Schedule",
            "path": "/instrument/admin/schedules"
          },
          {
            "label": "Swap",
            "path": "/instrument/admin/swaps"
          },
          {
            "label": "Swap Leg",
            "path": "/instrument/admin/swap-legs"
          },
          {
            "label": "Swaption",
            "path": "/instrument/admin/swaptions"
          },
          {
            "label": "Swaption Trade",
            "path": "/instrument/admin/swaption-trades"
          },
          {
            "label": "Trade",
            "path": "/instrument/admin/trades"
          },
          {
            "label": "Trade Amendment",
            "path": "/instrument/admin/trade-amendments"
          },
          {
            "label": "Trade Comment",
            "path": "/instrument/admin/trade-comments"
          },
          {
            "label": "Trade Confirmation",
            "path": "/instrument/admin/trade-confirmations"
          },
          {
            "label": "Trade Event",
            "path": "/instrument/admin/trade-events"
          },
          {
            "label": "Trade Fee",
            "path": "/instrument/admin/trade-fees"
          },
          {
            "label": "Trade Netting Membership",
            "path": "/instrument/admin/trade-netting-memberships"
          },
          {
            "label": "Trade Reset",
            "path": "/instrument/admin/trade-resets"
          },
          {
            "label": "Trade Source",
            "path": "/instrument/admin/trade-sources"
          },
          {
            "label": "Trade Version",
            "path": "/instrument/admin/trade-versions"
          },
          {
            "label": "Trade Workflow",
            "path": "/instrument/admin/trade-workflows"
          },
          {
            "label": "Trs Trade",
            "path": "/instrument/admin/trs-trades"
          }
        ]
      }
    ]
  },
  {
    "id": "risk",
    "label": "Risk",
    "accent": "#f44336",
    "iconName": "Shield",
    "children": [
      {
        "label": "Overview",
        "path": "/risk"
      },
      {
        "label": "Dashboard",
        "path": "/risk/dashboard"
      },
      {
        "label": "Scenario Analyzer",
        "path": "/risk/scenario-analyzer"
      },
      {
        "label": "Calibration Console",
        "path": "/risk/calibration-console"
      },
      {
        "label": "Entities",
        "path": "/risk/admin",
        "children": [
          {
            "label": "Calibration Result",
            "path": "/risk/admin/calibration-results"
          },
          {
            "label": "Greeks Snapshot",
            "path": "/risk/admin/greeks-snapshots"
          },
          {
            "label": "Model Definition",
            "path": "/risk/admin/model-definitions"
          },
          {
            "label": "Pricing Configuration",
            "path": "/risk/admin/pricing-configurations"
          },
          {
            "label": "Pricing Request",
            "path": "/risk/admin/pricing-requests"
          },
          {
            "label": "Pricing Result",
            "path": "/risk/admin/pricing-results"
          },
          {
            "label": "Risk Metric",
            "path": "/risk/admin/risk-metrics"
          },
          {
            "label": "Scenario",
            "path": "/risk/admin/scenarios"
          },
          {
            "label": "Scenario Result",
            "path": "/risk/admin/scenario-results"
          },
          {
            "label": "Sensitivity Grid",
            "path": "/risk/admin/sensitivity-grids"
          },
          {
            "label": "Stress Test",
            "path": "/risk/admin/stress-tests"
          },
          {
            "label": "Stress Test Result",
            "path": "/risk/admin/stress-test-results"
          }
        ]
      }
    ]
  },
  {
    "id": "structured-products",
    "label": "Structured Products",
    "accent": "#ab47bc",
    "iconName": "Box",
    "children": [
      {
        "label": "Overview",
        "path": "/structured-products"
      },
      {
        "label": "Dashboard",
        "path": "/structured-products/dashboard"
      },
      {
        "label": "Entities",
        "path": "/structured-products/admin",
        "children": [
          {
            "label": "Accumulator Config",
            "path": "/structured-products/admin/accumulator-configs"
          },
          {
            "label": "Approval",
            "path": "/structured-products/admin/approvals"
          },
          {
            "label": "Audit Trail",
            "path": "/structured-products/admin/audit-trails"
          },
          {
            "label": "Barrier",
            "path": "/structured-products/admin/barriers"
          },
          {
            "label": "Cashflow",
            "path": "/structured-products/admin/cashflows"
          },
          {
            "label": "Commodity Linked",
            "path": "/structured-products/admin/commodity-linkeds"
          },
          {
            "label": "Coupon Schedule",
            "path": "/structured-products/admin/coupon-schedules"
          },
          {
            "label": "Credit Linked",
            "path": "/structured-products/admin/credit-linkeds"
          },
          {
            "label": "Equity Structured",
            "path": "/structured-products/admin/equity-structureds"
          },
          {
            "label": "Fixing",
            "path": "/structured-products/admin/fixings"
          },
          {
            "label": "Fx Structured",
            "path": "/structured-products/admin/fx-structureds"
          },
          {
            "label": "Greeks",
            "path": "/structured-products/admin/greekses"
          },
          {
            "label": "Ird Structured",
            "path": "/structured-products/admin/ird-structureds"
          },
          {
            "label": "Lifecycle Event",
            "path": "/structured-products/admin/lifecycle-events"
          },
          {
            "label": "Mandatory Convertible Config",
            "path": "/structured-products/admin/mandatory-convertible-configs"
          },
          {
            "label": "Market Data Snapshot",
            "path": "/structured-products/admin/market-data-snapshots"
          },
          {
            "label": "Observation Schedule",
            "path": "/structured-products/admin/observation-schedules"
          },
          {
            "label": "Pricing Request Log",
            "path": "/structured-products/admin/pricing-request-logs"
          },
          {
            "label": "Pricing Result",
            "path": "/structured-products/admin/pricing-results"
          },
          {
            "label": "Product Link",
            "path": "/structured-products/admin/product-links"
          },
          {
            "label": "Product Template",
            "path": "/structured-products/admin/product-templates"
          },
          {
            "label": "Range Accrual Config",
            "path": "/structured-products/admin/range-accrual-configs"
          },
          {
            "label": "Scenario",
            "path": "/structured-products/admin/scenarios"
          },
          {
            "label": "Scenario Result",
            "path": "/structured-products/admin/scenario-results"
          },
          {
            "label": "Settlement",
            "path": "/structured-products/admin/settlements"
          },
          {
            "label": "Structured Product",
            "path": "/structured-products/admin/structured-products"
          },
          {
            "label": "Tarf Config",
            "path": "/structured-products/admin/tarf-configs"
          },
          {
            "label": "Termsheet",
            "path": "/structured-products/admin/termsheets"
          },
          {
            "label": "Underlying",
            "path": "/structured-products/admin/underlyings"
          }
        ]
      }
    ]
  },
  {
    "id": "alm",
    "label": "ALM",
    "accent": "#3f51b5",
    "iconName": "Scale",
    "children": [
      {
        "label": "Overview",
        "path": "/alm"
      },
      {
        "label": "Dashboard",
        "path": "/alm/dashboard"
      },
      {
        "label": "Eve Analysis",
        "path": "/alm/eve-analysis"
      },
      {
        "label": "Lcr Dashboard",
        "path": "/alm/lcr-dashboard"
      },
      {
        "label": "Nsfr Dashboard",
        "path": "/alm/nsfr-dashboard"
      },
      {
        "label": "Gap Analysis",
        "path": "/alm/gap-analysis"
      },
      {
        "label": "Position Upload",
        "path": "/alm/position-upload"
      },
      {
        "label": "Entities",
        "path": "/alm/admin",
        "children": [
          {
            "label": "A L M Position",
            "path": "/alm/admin/alm-positions"
          },
          {
            "label": "Audit Trail",
            "path": "/alm/admin/audit-trails"
          },
          {
            "label": "Basel Capital Requirement",
            "path": "/alm/admin/basel-capital-requirements"
          },
          {
            "label": "Behavioral Model",
            "path": "/alm/admin/behavioral-models"
          },
          {
            "label": "E V E Simulation",
            "path": "/alm/admin/eve-simulations"
          },
          {
            "label": "Expected Payment",
            "path": "/alm/admin/expected-payments"
          },
          {
            "label": "F T P Allocation",
            "path": "/alm/admin/ftp-allocations"
          },
          {
            "label": "F T P Rate",
            "path": "/alm/admin/ftp-rates"
          },
          {
            "label": "Gap Analysis",
            "path": "/alm/admin/gap-analysises"
          },
          {
            "label": "Lcr Runoff Rate",
            "path": "/alm/admin/lcr-runoff-rates"
          },
          {
            "label": "Liquidity Cashflow",
            "path": "/alm/admin/liquidity-cashflows"
          },
          {
            "label": "Liquidity Ratio",
            "path": "/alm/admin/liquidity-ratios"
          },
          {
            "label": "Monte Carlo Simulation",
            "path": "/alm/admin/monte-carlo-simulations"
          },
          {
            "label": "N I I Simulation",
            "path": "/alm/admin/nii-simulations"
          },
          {
            "label": "Reconciliation Result",
            "path": "/alm/admin/reconciliation-results"
          },
          {
            "label": "Reconciliation Rule",
            "path": "/alm/admin/reconciliation-rules"
          },
          {
            "label": "R W A Calculation",
            "path": "/alm/admin/rwa-calculations"
          },
          {
            "label": "Stress Scenario A L M",
            "path": "/alm/admin/stress-scenario-alms"
          },
          {
            "label": "Tolerance Rule",
            "path": "/alm/admin/tolerance-rules"
          },
          {
            "label": "Transaction",
            "path": "/alm/admin/transactions"
          },
          {
            "label": "Transaction Line Item",
            "path": "/alm/admin/transaction-line-items"
          }
        ]
      }
    ]
  },
  {
    "id": "collateral",
    "label": "Collateral",
    "accent": "#00897b",
    "iconName": "Landmark",
    "children": [
      {
        "label": "Overview",
        "path": "/collateral"
      },
      {
        "label": "Dashboard",
        "path": "/collateral/dashboard"
      },
      {
        "label": "Entities",
        "path": "/collateral/admin",
        "children": [
          {
            "label": "Collateral Balance",
            "path": "/collateral/admin/collateral-balances"
          },
          {
            "label": "Collateral Inventory",
            "path": "/collateral/admin/collateral-inventories"
          },
          {
            "label": "Collateral Movement",
            "path": "/collateral/admin/collateral-movements"
          },
          {
            "label": "Csa Agreement",
            "path": "/collateral/admin/csa-agreements"
          },
          {
            "label": "Csa Amendment History",
            "path": "/collateral/admin/csa-amendment-histories"
          },
          {
            "label": "Eligible Collateral",
            "path": "/collateral/admin/eligible-collaterals"
          },
          {
            "label": "Margin Call",
            "path": "/collateral/admin/margin-calls"
          },
          {
            "label": "Margin Call Dispute",
            "path": "/collateral/admin/margin-call-disputes"
          },
          {
            "label": "Netting Set",
            "path": "/collateral/admin/netting-sets"
          },
          {
            "label": "Netting Set Member",
            "path": "/collateral/admin/netting-set-members"
          },
          {
            "label": "Simm Calculation",
            "path": "/collateral/admin/simm-calculations"
          },
          {
            "label": "Simm Parameter",
            "path": "/collateral/admin/simm-parameters"
          },
          {
            "label": "Simm Sensitivity",
            "path": "/collateral/admin/simm-sensitivities"
          },
          {
            "label": "Valuation Adjustment",
            "path": "/collateral/admin/valuation-adjustments"
          },
          {
            "label": "Xva Result",
            "path": "/collateral/admin/xva-results"
          }
        ]
      }
    ]
  },
  {
    "id": "operations",
    "label": "Operations",
    "accent": "#e65100",
    "iconName": "Workflow",
    "children": [
      {
        "label": "Overview",
        "path": "/operations"
      },
      {
        "label": "Dashboard",
        "path": "/operations/dashboard"
      },
      {
        "label": "Entities",
        "path": "/operations/admin",
        "children": [
          {
            "label": "Bank Statement",
            "path": "/operations/admin/bank-statements"
          },
          {
            "label": "Bank Statement Line",
            "path": "/operations/admin/bank-statement-lines"
          },
          {
            "label": "Confirmation Matching",
            "path": "/operations/admin/confirmation-matchings"
          },
          {
            "label": "Confirmation Template",
            "path": "/operations/admin/confirmation-templates"
          },
          {
            "label": "Cutoff Time",
            "path": "/operations/admin/cutoff-times"
          },
          {
            "label": "E O D Run",
            "path": "/operations/admin/eod-runs"
          },
          {
            "label": "Fixing Management",
            "path": "/operations/admin/fixing-managements"
          },
          {
            "label": "Nostro Reconciliation",
            "path": "/operations/admin/nostro-reconciliations"
          },
          {
            "label": "Operational Alert",
            "path": "/operations/admin/operational-alerts"
          },
          {
            "label": "Operational Calendar",
            "path": "/operations/admin/operational-calendars"
          },
          {
            "label": "Reconciliation",
            "path": "/operations/admin/reconciliations"
          },
          {
            "label": "Reconciliation Break",
            "path": "/operations/admin/reconciliation-breaks"
          },
          {
            "label": "Reversal",
            "path": "/operations/admin/reversals"
          },
          {
            "label": "Settlement",
            "path": "/operations/admin/settlements"
          },
          {
            "label": "Settlement Fail",
            "path": "/operations/admin/settlement-fails"
          },
          {
            "label": "Settlement Hold",
            "path": "/operations/admin/settlement-holds"
          },
          {
            "label": "Settlement Instruction",
            "path": "/operations/admin/settlement-instructions"
          },
          {
            "label": "Settlement Reference Number",
            "path": "/operations/admin/settlement-reference-numbers"
          },
          {
            "label": "Stp Execution",
            "path": "/operations/admin/stp-executions"
          },
          {
            "label": "Stp Rule",
            "path": "/operations/admin/stp-rules"
          },
          {
            "label": "Swift Interface",
            "path": "/operations/admin/swift-interfaces"
          },
          {
            "label": "Swift Message Log",
            "path": "/operations/admin/swift-message-logs"
          }
        ]
      }
    ]
  },
  {
    "id": "limits-compliance",
    "label": "Limits Compliance",
    "accent": "#1565c0",
    "iconName": "Gauge",
    "children": [
      {
        "label": "Overview",
        "path": "/limits-compliance"
      },
      {
        "label": "Dashboard",
        "path": "/limits-compliance/dashboard"
      },
      {
        "label": "Entities",
        "path": "/limits-compliance/admin",
        "children": [
          {
            "label": "Compliance Check",
            "path": "/limits-compliance/admin/compliance-checks"
          },
          {
            "label": "Compliance Rule",
            "path": "/limits-compliance/admin/compliance-rules"
          },
          {
            "label": "Counterparty Exposure",
            "path": "/limits-compliance/admin/counterparty-exposures"
          },
          {
            "label": "Exception Request",
            "path": "/limits-compliance/admin/exception-requests"
          },
          {
            "label": "Limit Approval",
            "path": "/limits-compliance/admin/limit-approvals"
          },
          {
            "label": "Limit Breach",
            "path": "/limits-compliance/admin/limit-breaches"
          },
          {
            "label": "Limit Definition",
            "path": "/limits-compliance/admin/limit-definitions"
          },
          {
            "label": "Limit Exception",
            "path": "/limits-compliance/admin/limit-exceptions"
          },
          {
            "label": "Limit Hierarchy",
            "path": "/limits-compliance/admin/limit-hierarchies"
          },
          {
            "label": "Limit Utilization",
            "path": "/limits-compliance/admin/limit-utilizations"
          },
          {
            "label": "Portfolio Rule Assignment",
            "path": "/limits-compliance/admin/portfolio-rule-assignments"
          },
          {
            "label": "Trade Surveillance Alert",
            "path": "/limits-compliance/admin/trade-surveillance-alerts"
          }
        ]
      }
    ]
  },
  {
    "id": "accounting",
    "label": "Accounting",
    "accent": "#c62828",
    "iconName": "Calculator",
    "children": [
      {
        "label": "Overview",
        "path": "/accounting"
      },
      {
        "label": "Dashboard",
        "path": "/accounting/dashboard"
      },
      {
        "label": "Entities",
        "path": "/accounting/admin",
        "children": [
          {
            "label": "Account",
            "path": "/accounting/admin/accounts"
          },
          {
            "label": "Accounting Period",
            "path": "/accounting/admin/accounting-periods"
          },
          {
            "label": "Accounting Position",
            "path": "/accounting/admin/accounting-positions"
          },
          {
            "label": "Accrual Schedule",
            "path": "/accounting/admin/accrual-schedules"
          },
          {
            "label": "Hedge Designation",
            "path": "/accounting/admin/hedge-designations"
          },
          {
            "label": "Hedge Effectiveness Test",
            "path": "/accounting/admin/hedge-effectiveness-tests"
          },
          {
            "label": "Journal Entry",
            "path": "/accounting/admin/journal-entries"
          },
          {
            "label": "Journal Entry Line",
            "path": "/accounting/admin/journal-entry-lines"
          },
          {
            "label": "Ledger Balance",
            "path": "/accounting/admin/ledger-balances"
          },
          {
            "label": "Period Close Task",
            "path": "/accounting/admin/period-close-tasks"
          },
          {
            "label": "Posting Rule",
            "path": "/accounting/admin/posting-rules"
          },
          {
            "label": "Revaluation Entry",
            "path": "/accounting/admin/revaluation-entries"
          }
        ]
      }
    ]
  },
  {
    "id": "reporting",
    "label": "Reporting",
    "accent": "#00695c",
    "iconName": "FileBarChart",
    "children": [
      {
        "label": "Overview",
        "path": "/reporting"
      },
      {
        "label": "Entities",
        "path": "/reporting/admin",
        "children": [
          {
            "label": "Dashboard",
            "path": "/reporting/admin/dashboards"
          },
          {
            "label": "Dashboard Tile",
            "path": "/reporting/admin/dashboard-tiles"
          },
          {
            "label": "Regulatory Return",
            "path": "/reporting/admin/regulatory-returns"
          },
          {
            "label": "Regulatory Return Filing",
            "path": "/reporting/admin/regulatory-return-filings"
          },
          {
            "label": "Report Data Source",
            "path": "/reporting/admin/report-data-sources"
          },
          {
            "label": "Report Definition",
            "path": "/reporting/admin/report-definitions"
          },
          {
            "label": "Report Distribution",
            "path": "/reporting/admin/report-distributions"
          },
          {
            "label": "Report Output",
            "path": "/reporting/admin/report-outputs"
          },
          {
            "label": "Report Parameter",
            "path": "/reporting/admin/report-parameters"
          },
          {
            "label": "Report Run",
            "path": "/reporting/admin/report-runs"
          },
          {
            "label": "Report Schedule",
            "path": "/reporting/admin/report-schedules"
          }
        ]
      }
    ]
  },
  {
    "id": "order-management",
    "label": "Order Management",
    "accent": "#6a1b9a",
    "iconName": "ClipboardList",
    "children": [
      {
        "label": "Overview",
        "path": "/order-management"
      },
      {
        "label": "Dashboard",
        "path": "/order-management/dashboard"
      },
      {
        "label": "Entities",
        "path": "/order-management/admin",
        "children": [
          {
            "label": "Allocation Instruction",
            "path": "/order-management/admin/allocation-instructions"
          },
          {
            "label": "Allocation Line",
            "path": "/order-management/admin/allocation-lines"
          },
          {
            "label": "Allocation Template",
            "path": "/order-management/admin/allocation-templates"
          },
          {
            "label": "Execution",
            "path": "/order-management/admin/executions"
          },
          {
            "label": "Fill",
            "path": "/order-management/admin/fills"
          },
          {
            "label": "Order",
            "path": "/order-management/admin/orders"
          },
          {
            "label": "Order Audit Event",
            "path": "/order-management/admin/order-audit-events"
          },
          {
            "label": "Order Compliance Check",
            "path": "/order-management/admin/order-compliance-checks"
          },
          {
            "label": "Order Version",
            "path": "/order-management/admin/order-versions"
          },
          {
            "label": "Routing Rule",
            "path": "/order-management/admin/routing-rules"
          }
        ]
      }
    ]
  },
  {
    "id": "portfolio",
    "label": "Portfolio",
    "accent": "#2e7d32",
    "iconName": "PieChart",
    "children": [
      {
        "label": "Overview",
        "path": "/portfolio"
      },
      {
        "label": "Dashboard",
        "path": "/portfolio/dashboard"
      },
      {
        "label": "Portfolios",
        "path": "/portfolio/admin/portfolios"
      },
      {
        "label": "Trades",
        "path": "/portfolio/admin/trades"
      },
      {
        "label": "Entities",
        "path": "/portfolio/admin",
        "children": [
          {
            "label": "Aggregation Rule",
            "path": "/portfolio/admin/aggregation-rules"
          },
          {
            "label": "Calc Input Snapshot",
            "path": "/portfolio/admin/calc-input-snapshots"
          },
          {
            "label": "Corporate Action Election",
            "path": "/portfolio/admin/corporate-action-elections"
          },
          {
            "label": "Corporate Action Entitlement",
            "path": "/portfolio/admin/corporate-action-entitlements"
          },
          {
            "label": "Futures Position",
            "path": "/portfolio/admin/futures-positions"
          },
          {
            "label": "Holding",
            "path": "/portfolio/admin/holdings"
          },
          {
            "label": "Methodology",
            "path": "/portfolio/admin/methodologies"
          },
          {
            "label": "Multi Book",
            "path": "/portfolio/admin/multi-books"
          },
          {
            "label": "Performance Close",
            "path": "/portfolio/admin/performance-closes"
          },
          {
            "label": "Performance Run",
            "path": "/portfolio/admin/performance-runs"
          },
          {
            "label": "Pnl Waterfall",
            "path": "/portfolio/admin/pnl-waterfalls"
          },
          {
            "label": "Portfolio Book Map",
            "path": "/portfolio/admin/portfolio-book-maps"
          },
          {
            "label": "Portfolio Valuation",
            "path": "/portfolio/admin/portfolio-valuations"
          },
          {
            "label": "Position",
            "path": "/portfolio/admin/positions"
          },
          {
            "label": "Position F X",
            "path": "/portfolio/admin/position-fxes"
          },
          {
            "label": "Position I R",
            "path": "/portfolio/admin/position-irs"
          },
          {
            "label": "Position Snapshot",
            "path": "/portfolio/admin/position-snapshots"
          },
          {
            "label": "Position Transaction",
            "path": "/portfolio/admin/position-transactions"
          },
          {
            "label": "Rbor",
            "path": "/portfolio/admin/rbors"
          },
          {
            "label": "Risk Adjusted Metric",
            "path": "/portfolio/admin/risk-adjusted-metrics"
          },
          {
            "label": "Valuation Result",
            "path": "/portfolio/admin/valuation-results"
          }
        ]
      }
    ]
  },
  {
    "id": "qfinpricer-chief",
    "label": "Chief Agent",
    "accent": "#4fc3f7",
    "iconName": "Bot",
    "children": [
      {
        "label": "Overview",
        "path": "/qfinpricer-chief"
      },
      {
        "label": "Dashboard",
        "path": "/qfinpricer-chief/dashboard"
      },
      {
        "label": "Entities",
        "path": "/qfinpricer-chief/admin",
        "children": [
          {
            "label": "Agent",
            "path": "/qfinpricer-chief/admin/agents"
          },
          {
            "label": "Agent Invocation",
            "path": "/qfinpricer-chief/admin/agent-invocations"
          },
          {
            "label": "Execution Step",
            "path": "/qfinpricer-chief/admin/execution-steps"
          },
          {
            "label": "Orchestrator Chat",
            "path": "/qfinpricer-chief/admin/orchestrator-chats"
          }
        ]
      }
    ]
  },
  {
    "id": "wiki",
    "label": "Knowledge Wiki",
    "accent": "#66bb6a",
    "iconName": "BookMarked",
    "children": [
      {
        "label": "Overview",
        "path": "/wiki"
      },
      {
        "label": "Dashboard",
        "path": "/wiki/dashboard"
      },
      {
        "label": "Ingest",
        "path": "/wiki/ingest"
      },
      {
        "label": "Lint",
        "path": "/wiki/lint"
      },
      {
        "label": "Entities",
        "path": "/wiki/admin",
        "children": [
          {
            "label": "Link",
            "path": "/wiki/admin/links"
          },
          {
            "label": "Log",
            "path": "/wiki/admin/logs"
          },
          {
            "label": "Page",
            "path": "/wiki/admin/pages"
          },
          {
            "label": "Source",
            "path": "/wiki/admin/sources"
          }
        ]
      }
    ]
  },
  {
    "id": "instruments",
    "label": "Instruments",
    "accent": "#64748b",
    "iconName": "LayoutGrid",
    "children": [
      {
        "label": "Instruments",
        "path": "/instruments"
      },
      {
        "label": "Blotter",
        "path": "/instruments/blotter"
      },
      {
        "label": "Directory",
        "path": "/instruments/directory"
      },
      {
        "label": "Workbench",
        "path": "/instruments/workbench"
      },
      {
        "label": "Approvals",
        "path": "/instruments/approvals"
      },
      {
        "label": "Pricer",
        "path": "/instruments/pricer"
      },
      {
        "label": "Options Lab",
        "path": "/instruments/options-lab"
      },
      {
        "label": "Swap",
        "path": "/instruments/booking/swap"
      },
      {
        "label": "Swaps",
        "path": "/instruments/swaps"
      },
      {
        "label": "Bond",
        "path": "/instruments/booking/bond"
      },
      {
        "label": "Bonds",
        "path": "/instruments/bonds"
      },
      {
        "label": "Fra",
        "path": "/instruments/booking/fra"
      },
      {
        "label": "Fras",
        "path": "/instruments/fras"
      },
      {
        "label": "Capfloor",
        "path": "/instruments/booking/capfloor"
      },
      {
        "label": "Capfloors",
        "path": "/instruments/capfloors"
      },
      {
        "label": "Option",
        "path": "/instruments/booking/option"
      },
      {
        "label": "Options Book",
        "path": "/instruments/options-book"
      },
      {
        "label": "Swaption",
        "path": "/instruments/booking/swaption"
      },
      {
        "label": "Swaptions",
        "path": "/instruments/swaptions"
      },
      {
        "label": "Cds",
        "path": "/instruments/booking/cds"
      },
      {
        "label": "Cdss",
        "path": "/instruments/cdss"
      },
      {
        "label": "Swap Legacy",
        "path": "/instruments/booking/swap-legacy"
      },
      {
        "label": "Swaps Legacy",
        "path": "/instruments/swaps-legacy"
      },
      {
        "label": "Bond Legacy",
        "path": "/instruments/booking/bond-legacy"
      },
      {
        "label": "Bonds Legacy",
        "path": "/instruments/bonds-legacy"
      },
      {
        "label": "Fra Legacy",
        "path": "/instruments/booking/fra-legacy"
      },
      {
        "label": "Fras Legacy",
        "path": "/instruments/fras-legacy"
      },
      {
        "label": "Capfloor Legacy",
        "path": "/instruments/booking/capfloor-legacy"
      },
      {
        "label": "Capfloors Legacy",
        "path": "/instruments/capfloors-legacy"
      },
      {
        "label": "Option Legacy",
        "path": "/instruments/booking/option-legacy"
      },
      {
        "label": "Options Book Legacy",
        "path": "/instruments/options-book-legacy"
      },
      {
        "label": "Swaption Legacy",
        "path": "/instruments/booking/swaption-legacy"
      },
      {
        "label": "Swaptions Legacy",
        "path": "/instruments/swaptions-legacy"
      },
      {
        "label": "Cds Legacy",
        "path": "/instruments/booking/cds-legacy"
      },
      {
        "label": "Cdss Legacy",
        "path": "/instruments/cdss-legacy"
      },
      {
        "label": "Structured Products",
        "path": "/instruments/structured-products"
      },
      {
        "label": "Blotter",
        "path": "/instruments/structured-products/blotter"
      },
      {
        "label": "List",
        "path": "/instruments/structured-products/list"
      },
      {
        "label": "Booking",
        "path": "/instruments/structured-products/booking"
      },
      {
        "label": "Workbench",
        "path": "/instruments/structured-products/workbench"
      },
      {
        "label": "Pricing Lab",
        "path": "/instruments/structured-products/pricing-lab"
      },
      {
        "label": "Rfq",
        "path": "/instruments/structured-products/rfq"
      },
      {
        "label": "Approvals",
        "path": "/instruments/structured-products/approvals"
      }
    ]
  },
  {
    "id": "orchestrator",
    "label": "Orchestrator",
    "accent": "#64748b",
    "iconName": "LayoutGrid",
    "children": [
      {
        "label": "Orchestrator",
        "path": "/orchestrator"
      },
      {
        "label": "Dashboard",
        "path": "/orchestrator/dashboard"
      }
    ]
  },
  {
    "id": "system",
    "label": "System",
    "accent": "#64748b",
    "iconName": "Settings",
    "children": [
      {
        "label": "Home",
        "path": "/"
      },
      {
        "label": "Dashboard",
        "path": "/dashboard"
      },
      {
        "label": "Event Monitor",
        "path": "/events"
      },
      {
        "label": "Forge Admin",
        "path": "/forge/admin"
      },
      {
        "label": "Settings",
        "path": "/settings"
      }
    ]
  }
];

// ── Section icons ──────────────────────────────────────────────────────────────

export const SECTION_ICONS: Record<string, React.ReactElement> = {
  'reference-data': <BookOpen size={16} />,
  'market-data': <TrendingUp size={16} />,
  'instrument': <FileText size={16} />,
  'risk': <Shield size={16} />,
  'structured-products': <BoxIcon size={16} />,
  'alm': <Scale size={16} />,
  'collateral': <Landmark size={16} />,
  'operations': <Workflow size={16} />,
  'limits-compliance': <Gauge size={16} />,
  'accounting': <Calculator size={16} />,
  'order-management': <ClipboardList size={16} />,
  'portfolio': <PieChart size={16} />,
  'qfinpricer-chief': <Bot size={16} />,
  'wiki': <BookMarked size={16} />,
  'instruments': <Settings size={16} />,
  'orchestrator': <Settings size={16} />,
  'system': <Settings size={16} />,
};

// ── Derivations ────────────────────────────────────────────────────────────────

const PATH_LABELS: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (items: NavigationItem[]) => {
    for (const it of items) {
      map[it.path] = it.label;
      if (it.children) walk(it.children);
    }
  };
  for (const s of navigationConfig) walk(s.children);
  return map;
})();

/** Exact-path label lookup, derived from the nav tree (no hand-kept name map). */
export function labelForPath(path: string): string | undefined {
  return PATH_LABELS[path];
}

/** Section owning the given path — deepest matching prefix wins. '/' matches exactly. */
export function findSectionForPath(path: string): NavigationSection | undefined {
  let best: NavigationSection | undefined;
  let bestLen = -1;
  const consider = (section: NavigationSection, p: string) => {
    if (p === '/') {
      if (path === '/' && bestLen < 0) {
        best = section;
        bestLen = 0;
      }
      return;
    }
    if ((path === p || path.startsWith(p + '/')) && p.length > bestLen) {
      best = section;
      bestLen = p.length;
    }
  };
  const walk = (section: NavigationSection, items: NavigationItem[]) => {
    for (const it of items) {
      consider(section, it.path);
      if (it.children) walk(section, it.children);
    }
  };
  for (const s of navigationConfig) walk(s, s.children);
  return best;
}

export interface FlatSearchEntry {
  label: string;
  path: string;
  section: string;
}

/** Flattened nav entries — ready for a command palette. */
export function buildSearchIndex(): FlatSearchEntry[] {
  const out: FlatSearchEntry[] = [];
  const walk = (sectionLabel: string, items: NavigationItem[]) => {
    for (const it of items) {
      if (it.children?.length) walk(sectionLabel, it.children);
      else out.push({ label: it.label, path: it.path, section: sectionLabel });
    }
  };
  for (const s of navigationConfig) walk(s.label, s.children);
  return out;
}
